import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../services/api';

const FEEDBACK_COLORS: Record<string, string> = {
  open: '#2563eb',
  more_data_needed: '#d97706',
  closed: '#16a34a',
  reopened: '#dc2626',
};

export default function HrDashboard() {
  const [data, setData] = useState<any>({ calendar: [], feedbackByStatus: [], certificateExpiry: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/hr/dashboard')
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-600">Loading dashboard...</div>;

  const totalFeedback = data.feedbackByStatus.reduce((sum: number, f: any) => sum + Number(f.count), 0);
  const certExpiryChartData = data.certificateExpiry.map((c: any) => ({
    name: c.full_name,
    days: Math.max(0, Math.ceil((new Date(c.certificate_expiry_date).getTime() - Date.now()) / 86400000)),
  }));

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">HR Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-3">Upcoming Meetings &amp; Trainings</h2>
          <ul className="space-y-2 text-sm">
            {data.calendar.length === 0 && <li className="text-gray-500 text-center py-6">Nothing scheduled</li>}
            {data.calendar.map((c: any) => (
              <li key={`${c.kind}-${c.id}`} className="flex justify-between">
                <span>
                  {c.kind === 'training' ? '📚' : '🗓️'} {c.title || '(untitled)'}
                </span>
                <span className="text-gray-500">{new Date(c.date).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-3">Feedback Tickets by Status</h2>
          {totalFeedback === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">No tickets</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={data.feedbackByStatus}
                  dataKey="count"
                  nameKey="status"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  label={(entry: any) => `${entry.status.replace('_', ' ')} (${((entry.count / totalFeedback) * 100).toFixed(0)}%)`}
                >
                  {data.feedbackByStatus.map((f: any) => (
                    <Cell key={f.status} fill={FEEDBACK_COLORS[f.status] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend formatter={(value) => value.replace('_', ' ')} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="font-semibold mb-3">Training Certificate Expiry (90 days)</h2>
          {certExpiryChartData.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">Nothing expiring soon</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={certExpiryChartData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} label={{ value: 'Days left', position: 'insideBottom', offset: -5 }} />
                <YAxis type="category" dataKey="name" width={90} />
                <Tooltip />
                <Bar dataKey="days" fill="#d97706" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
